import { IsExpectedAuthError } from '../common/Helpers';
import { Constants } from '../internal/Constants';
import { CommonConstants, IUserDTO, Logger } from '@shared/common';
import { AuthClient, FrontendConstants, HttpError, Telemetry, UserClient } from '@shared/frontend';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { IAuthProvider } from '@shared/frontend';

/** File-local constants for AuthAdapter. */
const localConstants = {
  logContext: 'AuthAdapter',
  appLobby: 'lobby',
  appPortal: 'portal',
  telemetrySourceSignup: 'signup'
} as const;

/** Supported app kinds that share AuthAdapter. */
type AppKind = 'lobby' | 'portal';

/** Pending sign-in context for the next auth-state callback. */
interface IPendingSignInDTO {
  app: AppKind;
  pin?: string;
  smsConsentAccepted?: boolean;
}

/** Resolver pair used to complete explicit sign-in calls after backend login succeeds/fails. */
interface IPendingSignInResultDTO {
  resolve: () => void;
  reject: (err: unknown) => void;
}

/** Authentication context type definition. */
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: unknown | null;
  user: IUserDTO | null;
  authToken: string;
  updateUser: (user: IUserDTO) => void;
  signUpWithEmailPassword: (email: string, password: string, smsConsentAccepted?: boolean) => Promise<void>;
  signInWithEmailPassword: (email: string, password: string, pin?: string) => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  sendEmailVerification: () => Promise<void>;
  reloadUser: () => Promise<void>;
  logout: () => Promise<void>;
}

/** Authentication context instance. */
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * AuthAdapter component props.
 *
 * @param root0
 * @param root0.children The child components to be wrapped by the AuthAdapter.
 * @param root0.appKind - Which app is using this adapter.
 * @returns A JSX element wrapping children with AuthContext provider.
 */
export function AuthAdapter({ children, appKind = localConstants.appLobby }: { children: ReactNode; appKind?: AppKind }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);
  const [user, setUser] = useState<IUserDTO | null>(null);
  const [provider, setProvider] = useState<IAuthProvider | null>(null);
  const [authToken, setAuthToken] = useState<string>('');
  const pendingSignInRef = useRef<IPendingSignInDTO | null>(null);
  const pendingSignInResultRef = useRef<IPendingSignInResultDTO | null>(null);

  // Fetch (or clear) the Firebase ID token whenever authentication state changes
  useEffect(() => {
    if (!isAuthenticated) {
      setAuthToken('');
      return;
    }
    let cancelled = false;
    AuthClient.GetAuthClientAdapter()
      .then((adapter) => adapter.GetTokenSilently())
      .then((token) => {
        if (!cancelled && token) setAuthToken(token);
      })
      .catch(() => {
        // Token unavailable — leave empty.
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  // Initialize authentication provider and set up auth state listener
  useEffect(() => {
    const controller = new AbortController();
    let unsubscribe: (() => void) | null = null;
    let loginController: AbortController | null = null;

    // Initialize authentication provider
    setIsLoading(true);
    setError(null);
    AuthClient.GetAuthClientAdapter()
      .then((authProvider) => {
        if (controller.signal.aborted) return;
        setProvider(authProvider);
        // Subscribe to auth state changes — login to backend for full DB user
        unsubscribe = authProvider.OnAuthStateChanged((firebaseUser) => {
          if (controller.signal.aborted) return;

          // Abort any pending login call from a previous auth state change
          loginController?.abort();
          loginController = null;

          Logger.Debug(localConstants.logContext, `OnAuthStateChanged app=${appKind} firebaseUser=${firebaseUser ? 'present' : 'null'} pendingPin=${pendingSignInRef.current?.pin ? 'yes' : 'no'}`);

          if (firebaseUser) {
            // Firebase user authenticated — login to backend to create/refresh session and get full DB user
            loginController = new AbortController();
            const currentController = loginController;
            const pendingSignIn = pendingSignInRef.current;
            const loginContext = {
              app: appKind,
              pin: pendingSignIn?.pin,
              smsConsentAccepted: pendingSignIn?.smsConsentAccepted
            };

            UserClient.LoginUser(currentController.signal, firebaseUser, loginContext)
              .then(async (dbUser) => {
                if (controller.signal.aborted || currentController.signal.aborted) {
                  Logger.Debug(localConstants.logContext, `LoginUser resolved but aborted app=${appKind}`);
                  return;
                }
                // Gate on active status — inactive/banned users are signed out
                if (dbUser.status !== CommonConstants.Database.Status.Active) {
                  Logger.Warn(localConstants.logContext, `User ${dbUser.id} denied login (status: ${dbUser.status})`);
                  await authProvider.Logout();
                  setIsAuthenticated(false);
                  setUser(null);
                  setIsLoading(false);
                  pendingSignInResultRef.current?.reject(new Error('Failed to sign in'));
                  pendingSignInResultRef.current = null;
                  return;
                }
                Logger.Debug(localConstants.logContext, `LoginUser ok app=${appKind} userID=${dbUser.id}`);
                Telemetry.Identify({ internalUserID: dbUser.id, accountCreatedAt: dbUser.createdAt, emailVerified: dbUser.emailVerified });
                setIsAuthenticated(true);
                setUser(dbUser);
                setIsLoading(false);
                pendingSignInResultRef.current?.resolve();
                pendingSignInResultRef.current = null;
                pendingSignInRef.current = null;
              })
              .catch((err: unknown) => {
                if (controller.signal.aborted || currentController.signal.aborted) {
                  Logger.Debug(localConstants.logContext, `LoginUser rejected but aborted app=${appKind}`);
                  return;
                }
                if (Logger.GetErrorName(err) === 'AbortError') return;

                Logger.Debug(localConstants.logContext, `LoginUser failed app=${appKind} err=${Logger.GetErrorMessage(err)}`);

                // Portal-specific guard: an EXPLICIT portal sign-in that fails server-side PIN/privilege checks may fully sign the user out of Firebase. A SILENT session continuation that fails
                // (e.g. a lobby user whose shared Firebase session has not cleared portal PIN) must NOT sign out - that would cascade across the shared Firebase origin and log the user out of lobby
                // too. In the silent case we only clear local portal state so the gate shows the portal login screen, leaving every other app's session intact.
                const wasExplicitSignIn = pendingSignInResultRef.current !== null;
                const shouldForceLogout = appKind === localConstants.appPortal && wasExplicitSignIn;
                if (shouldForceLogout) {
                  authProvider.Logout().catch(() => undefined);
                }

                // Silent failure — automatic session continuation should not log errors.
                setIsAuthenticated(false);
                setUser(null);
                setIsLoading(false);
                pendingSignInResultRef.current?.reject(err);
                pendingSignInResultRef.current = null;
                pendingSignInRef.current = null;
              });
          } else {
            Logger.Debug(localConstants.logContext, `OnAuthStateChanged firebaseUser=null clearing state app=${appKind}`);
            setIsAuthenticated(false);
            setUser(null);
            setIsLoading(false);
          }
        });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err);
        setIsLoading(false);
      });

    // Cleanup function to abort async operations and unsubscribe on unmount
    return () => {
      controller.abort(); // Cancels async initialization
      loginController?.abort(); // Cancels any pending server login
      if (unsubscribe) unsubscribe(); // Cleans up listener
    };
  }, [appKind]);

  // Callbacks for authentication actions
  const sendPasswordResetEmail = useCallback(
    async (email: string) => {
      if (!provider) throw new Error(Constants.AuthNotInitialized);
      setError(null);
      try {
        await provider.SendPasswordResetEmail(email);
        Telemetry.PasswordResetSent();
      } catch (err: unknown) {
        Logger.Error(localConstants.logContext, `Password reset failed: ${Logger.GetErrorMessage(err)}`);
        setError(err);
        throw err;
      }
    },
    [provider]
  );

  // Send email verification
  const sendEmailVerification = useCallback(async () => {
    if (!provider) throw new Error(Constants.AuthNotInitialized);
    setError(null);
    try {
      await provider.SendEmailVerification();
      Telemetry.EmailVerificationSent();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, `Email verification failed: ${Logger.GetErrorMessage(err)}`);
      setError(err);
      throw err;
    }
  }, [provider]);

  // Re-authenticate and update the user's password
  const updatePassword = useCallback(
    async (currentPassword: string, newPassword: string) => {
      if (!provider) throw new Error(Constants.AuthNotInitialized);
      setError(null);
      try {
        await provider.UpdatePassword(currentPassword, newPassword);
      } catch (err: unknown) {
        Logger.Error(localConstants.logContext, `Password update failed: ${Logger.GetErrorMessage(err)}`);
        setError(err);
        throw err;
      }
    },
    [provider]
  );

  // Reload user from Firebase to refresh fields like emailVerified
  const reloadUser = useCallback(async () => {
    if (!provider) throw new Error(Constants.AuthNotInitialized);
    setError(null);
    try {
      const refreshed = await provider.ReloadUser();
      if (refreshed) setUser((prev) => (prev ? { ...prev, emailVerified: refreshed.emailVerified } : refreshed));
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, `User reload failed: ${Logger.GetErrorMessage(err)}`);
      setError(err);
      throw err;
    }
  }, [provider]);

  // Logout function — destroy server session before Firebase sign-out
  const logout = useCallback(async () => {
    if (!provider) return;
    setError(null);
    try {
      // Destroy server-side session first (requires valid auth token)
      const logoutController = new AbortController();
      await UserClient.LogoutUser(logoutController.signal);
    } catch (err: unknown) {
      // A 401 means there is no valid session to destroy (already signed out, for example via another same-origin app), which is exactly the desired end state -- record it quietly.
      if (err instanceof HttpError && err.status === FrontendConstants.HTTPStatusUnauthorized) {
        Logger.Debug(localConstants.logContext, 'Server logout skipped: no active session');
      } else if (Logger.GetErrorName(err) !== 'AbortError') {
        Logger.Error(localConstants.logContext, `Server logout failed: ${Logger.GetErrorMessage(err)}`);
      }
    }
    try {
      await provider.Logout();
    } catch (err: unknown) {
      Logger.Error(localConstants.logContext, `Firebase logout failed: ${Logger.GetErrorMessage(err)}`);
      setError(err);
      throw err;
    }
    // After successful logout, update authentication state
    Telemetry.Logout();
    setIsAuthenticated(false);
    setUser(null);
  }, [provider]);

  // Sign up with email and password
  const signUpWithEmailPassword = useCallback(
    async (email: string, password: string, smsConsentAccepted?: boolean) => {
      if (!provider) throw new Error(Constants.AuthNotInitialized);
      setError(null);
      try {
        pendingSignInRef.current = { app: appKind, smsConsentAccepted };
        await provider.SignUpWithEmailPassword(email, password);
        Telemetry.SignUpComplete();
        Telemetry.EmailVerificationSent(localConstants.telemetrySourceSignup);
        // Auth state listener will automatically update isAuthenticated and user
      } catch (err: unknown) {
        pendingSignInRef.current = null;
        // A duplicate email, weak password, or other validation rejection is expected user input (Debug); only a system/abuse condition (network, internal, too-many-requests) is a Warn.
        const message = `Sign up failed: ${Logger.GetErrorMessage(err)}`;
        if (IsExpectedAuthError(Logger.GetErrorCode(err))) {
          Logger.Debug(localConstants.logContext, message);
        } else {
          Logger.Warn(localConstants.logContext, message);
        }
        setError(err);
        throw err;
      }
    },
    [appKind, provider]
  );

  // Sign in with email and password
  const signInWithEmailPassword = useCallback(
    async (email: string, password: string, pin?: string) => {
      if (!provider) throw new Error(Constants.AuthNotInitialized);
      setError(null);
      try {
        // The backend login below is driven by `OnAuthStateChanged`, which only fires on an identity change. If a session is already persisted (e.g. a portal deep-link load where the silent,
        // PIN-less login was rejected, leaving Firebase signed in), re-signing in as the same user would NOT fire the listener — the `completion` promise would never settle and the form would
        // freeze. Sign out first so the subsequent sign-in always produces a fresh signed-out to signed-in transition that drives the login flow.
        if (await provider.IsAuthenticated()) {
          await provider.Logout();
        }
        const completion = new Promise<void>((resolve, reject) => {
          pendingSignInResultRef.current = { resolve, reject };
        });
        pendingSignInRef.current = { app: appKind, pin: pin?.trim() || undefined };
        await provider.SignInWithEmailPassword(email, password);
        await completion;
        Telemetry.Login();
      } catch (err: unknown) {
        pendingSignInResultRef.current = null;
        pendingSignInRef.current = null;
        // A rejected credential (wrong password, unknown user, etc.) is expected, routine user behaviour - Debug. Only a system/abuse condition (network, internal, too-many-requests) is a Warn. The
        // caller surfaces the failure in the UI, and the repeated-failure lockout logs the security-relevant signal at Warn.
        const message = `Sign in failed: ${Logger.GetErrorMessage(err)}`;
        if (IsExpectedAuthError(Logger.GetErrorCode(err))) {
          Logger.Debug(localConstants.logContext, message);
        } else {
          Logger.Warn(localConstants.logContext, message);
        }
        setError(err);
        throw err;
      }
    },
    [appKind, provider]
  );

  // Update user state after a successful profile update
  const updateUser = useCallback((user: IUserDTO) => {
    setUser(user);
  }, []);

  // Memoize context value to optimize performance
  const value = useMemo(() => {
    return {
      isAuthenticated,
      isLoading,
      error,
      user,
      authToken,
      updateUser,
      signUpWithEmailPassword,
      signInWithEmailPassword,
      sendPasswordResetEmail,
      updatePassword,
      sendEmailVerification,
      reloadUser,
      logout
    };
  }, [
    isAuthenticated,
    isLoading,
    error,
    user,
    authToken,
    updateUser,
    signUpWithEmailPassword,
    signInWithEmailPassword,
    sendPasswordResetEmail,
    updatePassword,
    sendEmailVerification,
    reloadUser,
    logout
  ]);

  // Render provider
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Custom hook to access authentication context.
 *
 * @returns The authentication context value.
 * @throws Error if used outside of AuthProvider.
 */
export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
